const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8080").replace(/\/$/, "");

async function parseJSON(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function fetchCsrfToken() {
  const response = await fetch(`${API_BASE_URL}/api/v1/csrf`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("could not load csrf token");
  }
  const payload = await parseJSON(response);
  const token = payload?.csrfToken;
  if (!token) {
    throw new Error("csrf token unavailable");
  }
  return token;
}

export async function apiRequest(path, {
  method = "GET",
  body,
  headers: customHeaders,
  requireCsrf = false,
  signal,
} = {}) {
  const headers = {
    Accept: "application/json",
    ...(customHeaders || {}),
  };

  let payload;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  if (requireCsrf) {
    headers["X-CSRF-Token"] = await fetchCsrfToken();
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    credentials: "include",
    body: payload,
    signal,
  });

  const data = await parseJSON(response);
  if (!response.ok) {
    const error = new Error(data?.error || "request failed");
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function loginAdmin(email, password) {
  return apiRequest("/api/v1/admin/auth/login", {
    method: "POST",
    requireCsrf: true,
    body: { email, password },
  });
}

export async function acceptAdminInvitation(token, password) {
  return apiRequest("/api/v1/admin/auth/invitations/accept", {
    method: "POST",
    requireCsrf: true,
    body: { token, password },
  });
}

export async function startAdminPasswordReset(email) {
  return apiRequest("/api/v1/admin/auth/password-reset/start", {
    method: "POST",
    requireCsrf: true,
    body: { email },
  });
}

export async function verifyAdminPasswordReset(email, code) {
  return apiRequest("/api/v1/admin/auth/password-reset/verify", {
    method: "POST",
    requireCsrf: true,
    body: { email, code },
  });
}

export async function completeAdminPasswordReset(email, password) {
  return apiRequest("/api/v1/admin/auth/password-reset/complete", {
    method: "POST",
    requireCsrf: true,
    body: { email, password },
  });
}

export async function getCurrentAdmin() {
  return apiRequest("/api/v1/admin/auth/me");
}

export async function getAdminOverview({
  range,
  from,
  to,
  timezone = "Africa/Lagos",
  signal,
} = {}) {
  const params = new URLSearchParams({ timezone });
  if (from && to) {
    params.set("from", from);
    params.set("to", to);
  } else {
    params.set("range", range || "14d");
  }
  return apiRequest(`/api/v1/admin/overview?${params.toString()}`, { signal });
}

function adminShopParams(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "" && value !== "all") {
      params.set(key, String(value));
    }
  });
  return params;
}

export async function listAdminShops(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/shops?${params.toString()}`, { signal });
}

export async function getAdminShop(shopId, signal) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}`, { signal });
}

export async function suspendAdminShop(shopId, reason) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}/suspend`, {
    method: "POST",
    requireCsrf: true,
    body: { reason },
  });
}

export async function reactivateAdminShop(shopId, reason) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}/reactivate`, {
    method: "POST",
    requireCsrf: true,
    body: { reason },
  });
}

export async function resetAdminShopAccess(shopId, reason) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}/reset-access`, {
    method: "POST",
    requireCsrf: true,
    body: { reason },
  });
}

export async function createAdminShopNote(shopId, body) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}/notes`, {
    method: "POST",
    requireCsrf: true,
    body: { body },
  });
}

export async function listAdminUsers(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/users?${params.toString()}`, { signal });
}

export async function createAdminUser(payload) {
  return apiRequest("/api/v1/admin/users", {
    method: "POST",
    requireCsrf: true,
    body: payload,
  });
}

export async function updateAdminUser(userId, payload) {
  return apiRequest(`/api/v1/admin/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    requireCsrf: true,
    body: payload,
  });
}

export async function listAdminAuditEvents(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/audit-events?${params.toString()}`, { signal });
}

export async function getAdminSettings(signal) {
  return apiRequest("/api/v1/admin/settings", { signal });
}

export async function updateAdminPlatformSettings(payload) {
  return apiRequest("/api/v1/admin/settings/platform", {
    method: "PATCH",
    requireCsrf: true,
    body: payload,
  });
}

export async function updateAdminPlanSettings(planId, payload) {
  return apiRequest(`/api/v1/admin/settings/plans/${encodeURIComponent(planId)}`, {
    method: "PATCH",
    requireCsrf: true,
    body: payload,
  });
}

export async function replaceAdminTerritories(payload) {
  return apiRequest("/api/v1/admin/settings/territories", {
    method: "PUT",
    requireCsrf: true,
    body: payload,
  });
}

export async function getPublicConfiguration(platform = "web", signal) {
  return apiRequest(`/api/v1/config/bootstrap?platform=${encodeURIComponent(platform)}`, { signal });
}

export async function logoutAdmin() {
  return apiRequest("/api/v1/admin/auth/logout", {
    method: "POST",
    requireCsrf: true,
  });
}

export async function listSubscriptionShops(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/shops/subscriptions?${params.toString()}`, { signal });
}

export async function assignShopSubscriptionPlan(shopId, planCode, reason) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}/subscription/plan`, {
    method: "POST",
    requireCsrf: true,
    body: { planCode, reason },
  });
}

export async function grantShopSubscriptionAccess(shopId, payload) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}/subscription/grant`, {
    method: "POST",
    requireCsrf: true,
    body: payload,
  });
}

export async function revokeShopSubscriptionAccess(shopId, payload = {}) {
  return apiRequest(`/api/v1/admin/shops/${encodeURIComponent(shopId)}/subscription/revoke`, {
    method: "POST",
    requireCsrf: true,
    body: payload,
  });
}

export async function listAdminPayments(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/payments?${params.toString()}`, { signal });
}

export async function reconcileAdminPayment(paymentId) {
  return apiRequest(`/api/v1/admin/payments/${encodeURIComponent(paymentId)}/reconcile`, {
    method: "POST",
    requireCsrf: true,
  });
}

export async function listAdminReferrals(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/referrals?${params.toString()}`, { signal });
}

export async function generateAdminReferralCode(input) {
  return apiRequest("/api/v1/admin/referrals/codes", {
    method: "POST",
    requireCsrf: true,
    body: input,
  });
}

export async function updateAdminReferral(referralId, input) {
  return apiRequest(`/api/v1/admin/referrals/${encodeURIComponent(referralId)}/actions`, {
    method: "POST",
    requireCsrf: true,
    body: input,
  });
}

export async function updateAdminReferralSettings(input) {
  return apiRequest("/api/v1/admin/referrals/settings", {
    method: "PATCH",
    requireCsrf: true,
    body: input,
  });
}

export async function deactivateAdminReferralCode(codeId) {
  return apiRequest(`/api/v1/admin/referrals/codes/${encodeURIComponent(codeId)}/deactivate`, {
    method: "POST",
    requireCsrf: true,
  });
}

export async function listAdminSalesAgents(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/sales/agents?${params.toString()}`, { signal });
}

export async function listAdminSupportTickets(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/support/tickets?${params.toString()}`, { signal });
}

export async function getAdminSupportTicket(ticketId, signal) {
  return apiRequest(`/api/v1/admin/support/tickets/${encodeURIComponent(ticketId)}`, { signal });
}

export async function updateAdminSupportTicket(ticketId, input) {
  return apiRequest(`/api/v1/admin/support/tickets/${encodeURIComponent(ticketId)}`, {
    method: "PATCH",
    requireCsrf: true,
    body: input,
  });
}

export async function replyAdminSupportTicket(ticketId, body, visibility = "public") {
  const idempotencyKey =
    (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
    `support-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return apiRequest(`/api/v1/admin/support/tickets/${encodeURIComponent(ticketId)}/messages`, {
    method: "POST",
    requireCsrf: true,
    body: { body, visibility },
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

export async function getAdminSyncOverview(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/sync/overview?${params.toString()}`, { signal });
}

export async function getAdminAnalytics(filters = {}, signal) {
  const params = adminShopParams(filters);
  return apiRequest(`/api/v1/admin/analytics?${params.toString()}`, { signal });
}

export async function requestAdminSyncCommand(clientId, input) {
  const idempotencyKey =
    (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
    `sync-command-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return apiRequest(`/api/v1/admin/sync/clients/${encodeURIComponent(clientId)}/commands`, {
    method: "POST",
    requireCsrf: true,
    body: input,
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

export async function updateAdminSyncIncident(incidentId, input) {
  return apiRequest(`/api/v1/admin/sync/incidents/${encodeURIComponent(incidentId)}/actions`, {
    method: "POST",
    requireCsrf: true,
    body: input,
  });
}

export async function createAdminSyncSupportCase(incidentId) {
  return apiRequest(`/api/v1/admin/sync/incidents/${encodeURIComponent(incidentId)}/support-case`, {
    method: "POST",
    requireCsrf: true,
  });
}

export async function createAdminSalesAgent(input) {
  return apiRequest("/api/v1/admin/sales/agents", {
    method: "POST",
    requireCsrf: true,
    body: input,
  });
}
